import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { format } from 'date-fns';
import PiscinaBuscador from '../components/PiscinaBuscador';
import Historico from './Historico';
import { useFincaId } from '../context/FincaContext';

/* ────────────────────────────────────────────
   Helpers ISO 8601
   ──────────────────────────────────────────── */
function isoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLunesYDomingoDeSemana(year, semana) {
  const ene4 = new Date(Date.UTC(year, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - dow + 1);

  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1) * 7);

  const domingo = new Date(lunes);
  domingo.setUTCDate(lunes.getUTCDate() + 6);

  return { lunes: isoDate(lunes), domingo: isoDate(domingo) };
}

const fmtCorto = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

export default function Muestreos() {
  const fincaIdContext = useFincaId();
  const [searchParams] = useSearchParams();

  /* ─── Tabs: listado | crecimientos (Leído desde la URL) ─── */
  const tabActiva = searchParams.get('tab') === 'crecimientos' ? 'crecimientos' : 'listado';

  /* ─── Estado del listado ─── */
  const hoy = new Date().toISOString().split('T')[0];
  const hace30 = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [muestreos, setMuestreos] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [piscinasConDatos, setPiscinasConDatos] = useState([]);
  const [piscinasCosechadasInfo, setPiscinasCosechadasInfo] = useState([]);
  const [cargandoCosechadas, setCargandoCosechadas] = useState(false);

  const [modoFecha, setModoFecha] = useState('rango');
  const [fechaDesde, setFechaDesde] = useState(hace30);
  const [fechaHasta, setFechaHasta] = useState(hoy);

  const [semanaDesdeKey, setSemanaDesdeKey] = useState('');
  const [semanaHastaKey, setSemanaHastaKey] = useState('');
  const [semanasDisponibles, setSemanasDisponibles] = useState([]);
  const [cargandoSemanas, setCargandoSemanas] = useState(false);

  const [sectorId, setSectorId] = useState('');
  const [piscinaId, setPiscinaId] = useState('');
  const [tipoPesaje, setTipoPesaje] = useState('');

  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [porcentaje, setPorcentaje] = useState('');
  const [biomasaSemana, setBiomasaSemana] = useState('');

  const piscinasCosechadasIds = useMemo(
    () => new Set(piscinasCosechadasInfo.map((p) => p.piscina_id)),
    [piscinasCosechadasInfo]
  );
  const sectoresConCosechadasIds = useMemo(
    () => new Set(piscinasCosechadasInfo.map((p) => p.sector_id)),
    [piscinasCosechadasInfo]
  );
  const sectoresConDatosIds = useMemo(
    () => new Set(piscinasConDatos.map((p) => p.sector_id)),
    [piscinasConDatos]
  );

  const sectoresVisibles = useMemo(() => {
    if (modoFecha === 'historial') {
      return sectores.filter((s) => sectoresConCosechadasIds.has(s.id));
    }
    return sectores.filter((s) => sectoresConDatosIds.has(s.id));
  }, [sectores, modoFecha, sectoresConCosechadasIds, sectoresConDatosIds]);

  const piscinasVisibles = useMemo(() => {
    const base = modoFecha === 'historial' ? piscinasCosechadasInfo : piscinasConDatos;
    if (!sectorId) return base;
    return base.filter((p) => p.sector_id === sectorId);
  }, [modoFecha, piscinasCosechadasInfo, piscinasConDatos, sectorId]);

  useEffect(() => {
    if (!sectorId) return;
    if (!sectoresVisibles.some((s) => s.id === sectorId)) {
      setSectorId('');
      setPiscinaId('');
    }
  }, [sectoresVisibles, sectorId]);

  /* ─── Cargas ─── */
  useEffect(() => {
    if (!fincaIdContext) return;
    cargarSectores(fincaIdContext);
    cargarPiscinasConDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    if (modoFecha !== 'historial') {
      setPiscinasCosechadasInfo([]);
      return;
    }
    let activo = true;
    setCargandoCosechadas(true);
    api.get('/muestreos/piscinas-cosechadas')
      .then(({ data }) => { if (activo) setPiscinasCosechadasInfo(Array.isArray(data) ? data : []); })
      .catch(() => { if (activo) setPiscinasCosechadasInfo([]); })
      .finally(() => { if (activo) setCargandoCosechadas(false); });
    return () => { activo = false; };
  }, [modoFecha, fincaIdContext]);

  useEffect(() => {
    if (modoFecha !== 'semana' && modoFecha !== 'rangoSemanas' && modoFecha !== 'historial') return;

    let activo = true;
    setCargandoSemanas(true);

    const params = {};
    if (sectorId) params.sector_id = sectorId;
    if (piscinaId) params.piscina_id = piscinaId;
    if (tipoPesaje) params.tipo_pesaje = tipoPesaje;
    if (modoFecha === 'historial') params.solo_cosechadas = 'true';

    api.get('/muestreos/semanas-con-datos', { params })
      .then(({ data }) => {
        if (!activo) return;
        const conRango = (data || []).map((s) => {
          const { lunes, domingo } = getLunesYDomingoDeSemana(s.year, s.semana);
          return { ...s, lunes, domingo, key: `${s.year}-${String(s.semana).padStart(2, '0')}` };
        });
        setSemanasDisponibles(conRango);
        if (conRango.length > 0) {
          const primera = conRango[0];
          if (!semanaDesdeKey) {
            setSemanaDesdeKey(primera.key);
            setFechaDesde(primera.lunes);
          }
          if (!semanaHastaKey) {
            setSemanaHastaKey(primera.key);
            setFechaHasta(primera.domingo);
          }
        }
      })
      .catch(() => { if (activo) setSemanasDisponibles([]); })
      .finally(() => { if (activo) setCargandoSemanas(false); });

    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoFecha, sectorId, piscinaId, tipoPesaje]);

  useEffect(() => {
    if (tabActiva !== 'listado') return;
    if (fechaDesde && fechaHasta) cargarMuestreos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta, sectorId, piscinaId, tipoPesaje, modoFecha, tabActiva]);

  const cargarSectores = async (idFinca) => {
    try {
      const { data } = await api.get(`/sectores/finca/${idFinca}`);
      setSectores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error cargando sectores:', err);
    }
  };

  const cargarPiscinasConDatos = async () => {
    try {
      const { data } = await api.get('/muestreos/piscinas-con-datos');
      setPiscinasConDatos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error cargando piscinas con datos:', err);
      setPiscinasConDatos([]);
    }
  };

  const cargarMuestreos = async () => {
    setCargando(true);
    try {
      const params = {};
      if (fechaDesde) params.desde = fechaDesde;
      if (fechaHasta) params.hasta = fechaHasta + 'T23:59:59';
      if (piscinaId) params.piscina_id = piscinaId;
      else if (sectorId) params.sector_id = sectorId;
      if (tipoPesaje) params.tipo_pesaje = tipoPesaje;
      if (modoFecha === 'historial') params.solo_cosechadas = 'true';

      const { data } = await api.get('/muestreos', { params });
      setMuestreos(data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  };

  const cambiarModo = (nuevo) => {
    setModoFecha(nuevo);
    if (nuevo === 'semana' || nuevo === 'rangoSemanas' || nuevo === 'historial') {
      setSemanaDesdeKey('');
      setSemanaHastaKey('');
    }
    if (nuevo !== 'historial') {
      setPiscinaId('');
    }
  };

  const cambiarSemanaUnica = (key) => {
    setSemanaDesdeKey(key);
    setSemanaHastaKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (sem) { setFechaDesde(sem.lunes); setFechaHasta(sem.domingo); }
  };

  const cambiarSemanaDesde = (key) => {
    setSemanaDesdeKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (!sem) return;
    setFechaDesde(sem.lunes);
    const semHasta = semanasDisponibles.find((s) => s.key === semanaHastaKey);
    if (!semHasta || sem.lunes > semHasta.lunes) {
      setSemanaHastaKey(key);
      setFechaHasta(sem.domingo);
    }
  };

  const cambiarSemanaHasta = (key) => {
    setSemanaHastaKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (!sem) return;
    setFechaHasta(sem.domingo);
    const semDesde = semanasDisponibles.find((s) => s.key === semanaDesdeKey);
    if (!semDesde || sem.domingo < semDesde.domingo) {
      setSemanaDesdeKey(key);
      setFechaDesde(sem.lunes);
    }
  };

  const limpiarFiltros = () => {
    setSectorId('');
    setPiscinaId('');
    setTipoPesaje('');
  };

  const abrirEditar = (m) => {
    setEditando(m);
    setPorcentaje(m.porcentaje != null ? String(m.porcentaje) : '');
    setBiomasaSemana(m.biomasa_semana_kg != null ? String(m.biomasa_semana_kg) : '');
  };

  const guardarEdicion = async () => {
    try {
      const payload = {
        porcentaje: porcentaje === '' ? null : parseFloat(porcentaje),
      };
      if (editando.tipo_pesaje === 'peso') {
        payload.biomasa_semana_kg = biomasaSemana === '' ? null : parseFloat(biomasaSemana);
      }
      await api.put(`/muestreos/${editando.id}`, payload);
      setEditando(null);
      setPorcentaje('');
      setBiomasaSemana('');
      await cargarMuestreos();
    } catch (err) {
      alert('Error al guardar');
    }
  };

  const eliminarMuestreo = async (m) => {
    const ok = confirm(
      `¿Eliminar este muestreo?\n\n` +
      `Piscina: ${m.piscina_codigo}\n` +
      `Fecha: ${format(new Date(m.fecha_hora), 'dd/MM/yyyy HH:mm')}\n\n` +
      `Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      await api.delete(`/muestreos/${m.id}`);
      await cargarMuestreos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar el muestreo');
    }
  };

  const descargarExcel = async () => {
    try {
      const params = {};
      if (fechaDesde) params.desde = fechaDesde;
      if (fechaHasta) params.hasta = fechaHasta + 'T23:59:59';
      if (piscinaId) params.piscina_id = piscinaId;
      else if (sectorId) params.sector_id = sectorId;
      if (tipoPesaje) params.tipo_pesaje = tipoPesaje;
      if (modoFecha === 'historial') params.solo_cosechadas = 'true';

      const response = await api.get('/muestreos/exportar/excel', { params, responseType: 'blob' });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `muestreos_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al exportar Excel:', err);
      alert('Error al exportar a Excel. Revisa la consola.');
    }
  };

  const badgeTipo = (tipo, compacto = false) => {
    const esPrepeso = tipo === 'prepeso';
    return (
      <span
        className={`inline-block rounded font-semibold ${
          compacto ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
        } ${
          esPrepeso ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
        }`}
      >
        {esPrepeso ? (compacto ? 'Pre' : 'Prepeso') : 'Peso'}
      </span>
    );
  };

  const mostrarBiomasa = muestreos.some((m) => m.tipo_pesaje === 'peso');

  const semDesde = semanasDisponibles.find((s) => s.key === semanaDesdeKey);
  const semHasta = semanasDisponibles.find((s) => s.key === semanaHastaKey);
  const aniosDisponibles = [...new Set(semanasDisponibles.map((s) => s.year))].sort((a, b) => b - a);

  const inputBase = 'w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
  const labelBase = 'text-[10px] font-medium text-gray-500';

  return (
    <div className="flex flex-col h-full w-full min-h-0">
      <div className="flex-1 min-h-0 overflow-auto bg-gray-100">
        
        {/* TAB: LISTADO */}
        {tabActiva === 'listado' && (
          <div className="p-6 flex flex-col h-full min-h-0">
            {/* Filtros (Fijos) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 p-4 shrink-0">
              <div className="flex items-center gap-6 flex-wrap justify-center">

                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => cambiarModo('rango')}
                    className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${
                      modoFecha === 'rango' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📆 Rango días
                  </button>
                  <button
                    type="button"
                    onClick={() => cambiarModo('semana')}
                    className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${
                      modoFecha === 'semana' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📊 Semana
                  </button>
                  <button
                    type="button"
                    onClick={() => cambiarModo('rangoSemanas')}
                    className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${
                      modoFecha === 'rangoSemanas' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📈 Rango semanas
                  </button>
                  <button
                    type="button"
                    onClick={() => cambiarModo('historial')}
                    className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${
                      modoFecha === 'historial' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📚 Historial
                  </button>
                </div>

                <div className="flex flex-col gap-1.5 min-w-[150px]">
                  {modoFecha === 'rango' && (
                    <>
                      <label className="block">
                        <span className={labelBase}>Desde</span>
                        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className={`mt-0.5 ${inputBase}`} />
                      </label>
                      <label className="block">
                        <span className={labelBase}>Hasta</span>
                        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className={`mt-0.5 ${inputBase}`} />
                      </label>
                    </>
                  )}

                  {modoFecha === 'semana' && (
                    <>
                      <label className="block">
                        <span className={labelBase}>Semana</span>
                        <select
                          value={semanaDesdeKey}
                          onChange={(e) => cambiarSemanaUnica(e.target.value)}
                          disabled={cargandoSemanas}
                          className={`mt-0.5 min-w-[260px] ${inputBase}`}
                        >
                          {cargandoSemanas ? <option value="">Cargando...</option>
                            : semanasDisponibles.length === 0 ? <option value="">Sin datos</option>
                            : (<>
                                <option value="">— Seleccionar —</option>
                                {aniosDisponibles.map((anio) => {
                                  const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
                                  return (
                                    <optgroup key={anio} label={`Año ${anio}`}>
                                      {semanasAnio.map((s) => (
                                        <option key={s.key} value={s.key}>
                                          Semana {String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)} · {s.total} reg.
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                })}
                              </>)}
                        </select>
                      </label>
                      {semDesde && (
                        <div className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 whitespace-nowrap">
                          📊 Semana {String(semDesde.semana).padStart(2, '0')} · {semDesde.year}: {fmtCorto(semDesde.lunes)} → {fmtCorto(semDesde.domingo)}
                        </div>
                      )}
                    </>
                  )}

                  {(modoFecha === 'rangoSemanas' || modoFecha === 'historial') && (
                    <>
                      <label className="block">
                        <span className={labelBase}>Semana desde</span>
                        <select
                          value={semanaDesdeKey}
                          onChange={(e) => cambiarSemanaDesde(e.target.value)}
                          disabled={cargandoSemanas}
                          className={`mt-0.5 min-w-[220px] ${inputBase}`}
                        >
                          {cargandoSemanas ? <option value="">Cargando...</option>
                            : semanasDisponibles.length === 0 ? <option value="">Sin datos</option>
                            : (<>
                                <option value="">— Desde —</option>
                                {aniosDisponibles.map((anio) => {
                                  const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
                                  return (
                                    <optgroup key={anio} label={`Año ${anio}`}>
                                      {semanasAnio.map((s) => (
                                        <option key={s.key} value={s.key}>
                                          S{String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)}
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                })}
                              </>)}
                        </select>
                      </label>
                      <label className="block">
                        <span className={labelBase}>Semana hasta</span>
                        <select
                          value={semanaHastaKey}
                          onChange={(e) => cambiarSemanaHasta(e.target.value)}
                          disabled={cargandoSemanas}
                          className={`mt-0.5 min-w-[220px] ${inputBase}`}
                        >
                          {cargandoSemanas ? <option value="">Cargando...</option>
                            : semanasDisponibles.length === 0 ? <option value="">Sin datos</option>
                            : (<>
                                <option value="">— Hasta —</option>
                                {aniosDisponibles.map((anio) => {
                                  const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
                                  return (
                                    <optgroup key={anio} label={`Año ${anio}`}>
                                      {semanasAnio.map((s) => (
                                        <option key={s.key} value={s.key}>
                                          S{String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)}
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                })}
                              </>)}
                        </select>
                      </label>
                      {semDesde && semHasta && (
                        <div className={`text-[10px] border rounded-lg px-2 py-1 whitespace-nowrap ${
                          modoFecha === 'historial'
                            ? 'text-purple-700 bg-purple-50 border-purple-200'
                            : 'text-blue-700 bg-blue-50 border-blue-200'
                        }`}>
                          {modoFecha === 'historial'
                            ? `📚 Historial: Sem ${String(semDesde.semana).padStart(2, '0')} a Sem ${String(semHasta.semana).padStart(2, '0')} · ${semDesde.year}`
                            : `📈 Sem ${String(semDesde.semana).padStart(2, '0')} → Sem ${String(semHasta.semana).padStart(2, '0')} · ${semDesde.year}`}
                          {' '}({fmtCorto(semDesde.lunes)} → {fmtCorto(semHasta.domingo)})
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex flex-col gap-1.5 min-w-[180px]">
                  <label className="block">
                    <span className={labelBase}>Sector</span>
                    <select value={sectorId} onChange={(e) => setSectorId(e.target.value)} className={`mt-0.5 ${inputBase}`}>
                      <option value="">Todos</option>
                      {sectoresVisibles.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </select>
                  </label>
                  <div className="relative z-50">
                    <PiscinaBuscador
                      piscinas={piscinasVisibles}
                      value={piscinaId}
                      onChange={(id) => setPiscinaId(id)}
                      disabled={!sectorId}
                      permitirTodas={true}
                      valorTodas=""
                      labelTodas={
                        modoFecha === 'historial'
                          ? (cargandoCosechadas ? 'Cargando piscinas...' : 'Todas las cosechadas')
                          : 'Todas las piscinas con datos'
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 min-w-[140px]">
                  <label className="block">
                    <span className={labelBase}>Tipo</span>
                    <select value={tipoPesaje} onChange={(e) => setTipoPesaje(e.target.value)} className={`mt-0.5 ${inputBase}`}>
                      <option value="">Todos</option>
                      <option value="peso">Peso</option>
                      <option value="prepeso">Prepeso</option>
                    </select>
                  </label>
                  {(sectorId || piscinaId || tipoPesaje) && (
                    <button
                      onClick={limpiarFiltros}
                      className="text-[11px] text-gray-500 hover:text-gray-700 underline self-start"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>

                {/* ✅ BOTÓN DE EXCEL ESTILO CRECIMIENTOS */}
                <div className="flex flex-col justify-end ml-auto">
                  <button
                    type="button"
                    onClick={descargarExcel}
                    title="Exportar a Excel"
                    className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center h-[34px]"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="2" fill="white" />
                      <path
                        d="M8 8l8 8M16 8l-8 8"
                        stroke="#16A34A"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>

              </div>
            </div>

            {/* Tabla con scroll interno y thead sticky */}
            <div className="bg-white rounded shadow overflow-hidden flex flex-col flex-1 min-h-0">
              {cargando ? (
                <div className="p-6 text-center text-gray-500">Cargando...</div>
              ) : muestreos.length === 0 ? (
                <div className="p-6 text-center text-gray-500">No hay muestreos con los filtros seleccionados.</div>
              ) : (
                <div className="overflow-auto flex-1 min-h-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                        <th className="text-center px-2 py-2 font-semibold" style={{ width: 80 }}>Tipo</th>
                        <th className="text-left px-3 py-2 font-semibold">Sector</th>
                        <th className="text-left px-3 py-2 font-semibold">Piscina</th>
                        <th className="text-left px-3 py-2 font-semibold">Técnico</th>
                        <th className="text-right px-3 py-2 font-semibold">Peso prom (g)</th>
                        <th className="text-right px-3 py-2 font-semibold">Cant. Promedio</th>
                        <th className="text-right px-3 py-2 font-semibold">% Sobrev.</th>
                        {mostrarBiomasa && (
                          <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Biomasa semana (lb)</th>
                        )}
                        <th className="text-right px-3 py-2 font-semibold">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {muestreos.map((m) => {
                        const esPeso = m.tipo_pesaje === 'peso';
                        return (
                          <tr key={m.id} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(new Date(m.fecha_hora), 'dd/MM/yyyy HH:mm')}
                            </td>
                            <td className="px-2 py-2 text-center">{badgeTipo(m.tipo_pesaje, true)}</td>
                            <td className="px-3 py-2">{m.sector_nombre}</td>
                            <td className="px-3 py-2 font-medium">{m.piscina_codigo}</td>
                            <td className="px-3 py-2 text-gray-600">{m.usuario_nombre}</td>
                            <td className="px-3 py-2 text-right">
                              {Number(m.peso_promedio_g).toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-right">{m.cantidad_muestreada}</td>
                            <td className="px-3 py-2 text-right font-semibold">
                              {m.porcentaje != null ? (
                                <span className="text-green-700">{Math.round(Number(m.porcentaje))}%</span>
                              ) : (
                                <span className="text-gray-400 text-xs">pendiente</span>
                              )}
                            </td>
                            {mostrarBiomasa && (
                              <td className="px-3 py-2 text-right">
                                {esPeso && m.biomasa_semana_kg != null ? (
                                  <span className="font-medium text-indigo-700">
                                    {Number(m.biomasa_semana_kg).toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button
                                onClick={() => abrirEditar(m)}
                                className="text-blue-600 hover:underline text-xs mr-3"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => eliminarMuestreo(m)}
                                className="text-red-600 hover:underline text-xs"
                              >
                                Eliminar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: CRECIMIENTOS */}
        {tabActiva === 'crecimientos' && <Historico />}
      </div>

      {/* Modal editar (solo en tab Listado) */}
      {editando && tabActiva === 'listado' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-2">Editar muestreo</h2>
            <p className="text-sm text-gray-600 mb-4">
              Piscina <strong>{editando.piscina_codigo}</strong> ·{' '}
              {format(new Date(editando.fecha_hora), 'dd/MM/yyyy HH:mm')}
              {' · '}
              {badgeTipo(editando.tipo_pesaje)}
            </p>

            <label className="block mb-4">
              <span className="text-sm font-medium">Porcentaje sobrevivencia (%)</span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={porcentaje}
                onChange={(e) => setPorcentaje(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Ej: 12.5"
              />
              <span className="text-xs text-gray-500">Dejar vacío para quitar el valor</span>
            </label>

            {editando.tipo_pesaje === 'peso' && (
              <label className="block mb-4">
                <span className="text-sm font-medium">Biomasa semanal (lb)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={biomasaSemana}
                  onChange={(e) => setBiomasaSemana(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ej: 145.50"
                />
                <span className="text-xs text-gray-500">
                  Solo se registra en muestreos de tipo <strong>Peso</strong>.
                </span>
              </label>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setEditando(null); setPorcentaje(''); setBiomasaSemana(''); }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={guardarEdicion}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}